import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Channel,
  deviceUtils,
  Envs,
  EventType,
  LayoutComponentProps,
  useAppContext,
  useChannelContext,
  useEnvContext,
  useEventBusEmit,
} from 'scbk-services';
import { STATUS_BAR_COLOR } from 'scbk-shared-module/constants';

import { DesktopFooter } from 'scbk-shared-module/layout/footer/DesktopFooter';
import { MainHeader } from 'scbk-shared-module/layout/top-header/MainHeader';
import { cn, SkipNavigation } from 'scbk-ui-web';

type mainBg = 'main-page-wrap' | 'sub-main-page' | 'default-page' | 'comfy-banking-page';

interface MainLayoutProps extends LayoutComponentProps {
  mainType?: mainBg;
  mainHeaderType?: 'pageMain' | 'subMain' | 'comfyBanking';
  isGradient?: boolean;
  isShadow?: boolean;
  darkMode?: boolean;
  className?: string;
  enableLargeText?: boolean;
  topBar?: boolean;
}

const MainLayout = ({
  mainType = 'default-page',
  mainHeaderType,
  children,
  title,
  isShadow,
  darkMode = false,
  className,
  enableLargeText = false,
  topBar = false,
}: MainLayoutProps) => {
  const { channel } = useChannelContext();
  const { seniorMode, osInfo, getContext } = useAppContext();
  const { getEnv } = useEnvContext();
  const isDesktop = deviceUtils.isPC();
  const isLargeText = useMemo(() => {
    return seniorMode === 'Y';
  }, [seniorMode]);

  const applied = useMemo(() => {
    return enableLargeText && isLargeText && !isDesktop; // 260803 추가
  }, [isDesktop, isLargeText, enableLargeText]);

  const mainRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [_scrollDistanceFromBottom, setScrollDistanceFromBottom] = useState(999999);
  const emit = useEventBusEmit();

  useEffect(() => {
    //
    if (channel !== Channel.MB) {
      return;
    }

    const theme = STATUS_BAR_COLOR[darkMode ? 'dark' : 'light'][mainType];
    const color = isScrolled ? (theme.scrolled ?? '0') : (theme.default ?? '0');

    //0:black	1:white
    emit(EventType.ChangeStatusBarColor, color);
  }, [channel, isScrolled, emit, mainType, darkMode]);

  useEffect(() => {
    const handleScroll = () => {
      let scrolled;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      //스크롤 위치 확인
      if (isDesktop) {
        scrolled = scrollTop > 16;
      } else {
        scrolled = scrollTop > 24;
      }

      setIsScrolled(scrolled);
      setScrollDistanceFromBottom(distanceFromBottom);
    };

    window.addEventListener('scroll', handleScroll);

    //초기 체크
    handleScroll();

    //리사이즈 감지
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  return (
    <>
      {isDesktop && <SkipNavigation />}
      <div className="wrap">
        <MainHeader
          title={title}
          mainHeaderType={mainHeaderType}
          isNew
          isScrolled={isScrolled}
          isShadow={isShadow}
          darkMode={darkMode}
          topBar={topBar}
        />
        <main
          className={cn(
            mainType,
            isScrolled ? 'scrolled' : '',
            className,
            darkMode === true && 'darkMode',
          )}
          ref={mainRef}
          data-text-display={String(applied)}
          id="content"
        >
          {children}
        </main>
        {!isDesktop && getEnv() !== Envs.PROD && (
          <div className="floating-version">{`${osInfo?.osType ?? ''} : ${getContext().app?.appVersion}`}</div>
        )}
        {isDesktop && <DesktopFooter />}
      </div>
    </>
  );
};

export { MainLayout };
